import { requestSaveCda } from '@/api/cdaApi';
import { uploadFile, getFileById } from '@/api/file/fileApi';
import { INIT_NO_OP_NUM, CDA_FILE_TYPE } from '@/config';
import { setCda, clearCda } from '@/mutation-types';
import kinds from '@/constants/kinds';
import {
  isVitalCcdForCda,
  resolveProcedureMeasuresForSidebar,
  resolveMeasureSidebarDate,
} from '@/utils/labClassification';

const state = {
  cdaId: INIT_NO_OP_NUM,    // id in db
  cdaIndex: INIT_NO_OP_NUM, // id in dropdown list
};

const getters = {
  getCdaId: state => state.cdaId,
  getCdaIndex: state => state.cdaIndex,
  /**
   * Groups cda side bar content by date and kind
   * @returns {object} {2018-10-11: {'procedure':[], 'measures':[], 'diagnosis': []...}, }
   */
  getCdaSideBarContentGrouped (state, getters, rootState) {

    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const ccds = rootState.login.phrData.ccds;
    const cda = rootState.login.phrData.cdas?.[state.cdaIndex];
    if (!cda) return;
    const bdaks = cda.byDateAndKind;
    const measures = (cda.isSaved)?rootState.login.phrData.measures:cda.emeasures;
    const procedures = (cda.isSaved)?rootState.login.phrData.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?rootState.login.phrData.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?rootState.login.phrData.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?rootState.login.phrData.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?rootState.login.phrData.medications:cda.emeds;
    const allergies = (cda.isSaved)?rootState.login.phrData.allergies:cda.eallergies;
    // Lab vs vitals bucketing uses shared classification (see utils/labClassification.js).
    const vitalCcds = (rootState.login.watch && rootState.login.watch.vitalSignCodes) || [];

    let sideBarContent = {};
    Object.keys(bdaks).forEach(k => {
      let bdak = bdaks[k];
      if (bdak.lo === null || typeof bdak.lo === 'undefined' ) {
        bdak.lo = 'Undated';
      }
      if (sideBarContent[bdak.lo] === null || typeof sideBarContent[bdak.lo] === 'undefined') {
        sideBarContent[bdak.lo] = {};
      }

      const loBucket = sideBarContent[bdak.lo];
      let hasCcd = false;
      let entry = { kind: bdak.kind };

      if (bdak.kind === kinds.E_PROCEDURE || bdak.kind === kinds.PROCEDURE) {
        const ccdId = procedures[bdak.id].ccd;
        if (ccdId) {
          const measureKind = bdak.kind === kinds.PROCEDURE ? kinds.MEASURE : kinds.E_MEASURE;
          const labKind = bdak.kind === kinds.PROCEDURE ? kinds.LAB : kinds.E_LAB;
          const measIds = procmeas[bdak.id] || [];
          const { vitalMeasures, hideProcedure } = resolveProcedureMeasuresForSidebar({
            measIds,
            measures,
            procCcd: ccdId,
            procKind: bdak.kind,
            measureKind,
            lo: bdak.lo,
            ccds,
            vitalSignCodes: vitalCcds,
            labKind,
            sideBarContent,
            flatBuckets: true,
            procedureMeta: procedures[bdak.id],
            cdaIndex: state.cdaIndex,
            procmeas,
            procedures,
          });
          if (hideProcedure) {
            return;
          }
          hasCcd = true;
          entry = {
            id: bdak.id,
            cdaIndex: state.cdaIndex,
            ccd: ccdId,
            desc: (ccds[ccdId] || {}).desc || String(ccdId),
            kind: bdak.kind,
            ...procedures[bdak.id],
            measures: vitalMeasures,
          };
        }
      } else if (bdak.kind === kinds.E_MEASURE || bdak.kind === kinds.MEASURE) {
        const ccdId = measures[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          const labKind = bdak.kind === kinds.MEASURE ? kinds.LAB : kinds.E_LAB;
          const bucketKind = isVitalCcdForCda(ccdId, vitalCcds) ? bdak.kind : labKind;
          const meas = measures[bdak.id];
          const measLo = resolveMeasureSidebarDate(meas, bdak.id, {
            procmeas,
            procedures,
            measures,
            ccds,
            fallbackLo: bdak.lo,
          });
          entry = {
            dname: (ccds[ccdId] || {}).desc || String(ccdId),
            kind: bdak.kind,
            ...meas,
            lo: measLo,
            weight: meas.weight ? meas.weight : 0,
          };
          if (!sideBarContent[measLo]) sideBarContent[measLo] = {};
          if (!sideBarContent[measLo][bucketKind]) sideBarContent[measLo][bucketKind] = [];
          sideBarContent[measLo][bucketKind].push(entry);
          if (bucketKind === bdak.kind) {
            sideBarContent[measLo][bucketKind] = _.orderBy(sideBarContent[measLo][bucketKind], 'weight', 'desc');
          }
          hasCcd = false;
        }
      } else if (bdak.kind === kinds.E_DIAGNOSIS || bdak.kind === kinds.DIAGNOSIS) {
        // for the 0 in codes[0], because of icd9,10, different codes have same/similar desc; choose first entry
        let ccdId = diagnosiss[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...diagnosiss[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_MEDICATION || bdak.kind === kinds.MEDICATION) {
        let ccdId = medications[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...medications[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_VACCINE || bdak.kind === kinds.VACCINE) {
        let ccdId = bdak.kind === kinds.VACCINE?vaccines[bdak.id].codes[0]:vaccines[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...vaccines[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_ALLERGY || bdak.kind === kinds.ALLERGY) {
        let ccdId = allergies[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...allergies[bdak.id]};
        }
      }
      if (hasCcd) {
        if (!loBucket[bdak.kind]) {
          loBucket[bdak.kind] = [];
        }
        loBucket[bdak.kind].push(entry);
      }
    });
    return sideBarContent;
  },
  getCdaSideBarContentKindAndDate (state, getters, rootState) {
    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const ccds = rootState.login.phrData.ccds;
    const cda = rootState.login.phrData.cdas?.[state.cdaIndex];
    if (!cda) return;
    const bkads = cda.byKindAndDate;
    const measures = (cda.isSaved)?rootState.login.phrData.measures:cda.emeasures;
    const procedures = (cda.isSaved)?rootState.login.phrData.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?rootState.login.phrData.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?rootState.login.phrData.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?rootState.login.phrData.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?rootState.login.phrData.medications:cda.emeds;
    const allergies = (cda.isSaved)?rootState.login.phrData.allergies:cda.eallergies;

    // Vital-sign CCDs (LA_labccd.ZV_labccd_vitalsign = true) loaded via requestVitalSignsCodes.
    // Measures whose CCD is not a vital sign are lab results and belong under "Labs".
    // If the codes have not loaded yet, fall back to treating every measure as a vital
    // so nothing is mislabeled while the list is empty.
    // Lab vs vitals bucketing uses shared classification (see utils/labClassification.js).
    const vitalCcds = (rootState.login.watch && rootState.login.watch.vitalSignCodes) || [];

    let sideBarContent = {};
    let entry = {};
    Object.keys(bkads).forEach(kind => {
      let bkad = bkads[kind];
      sideBarContent[kind]= {};

      entry = {};
      entry.kind = kind;
      sideBarContent.kind = {};

      if (kind === kinds.E_PROCEDURE || kind === kinds.PROCEDURE) {

        bkad.forEach(item => {
          const ccdId = procedures[item.id].ccd;
          if (ccdId) {
            const lo = item.lo || 'Undated';
            const measureKind = kind === kinds.PROCEDURE ? kinds.MEASURE : kinds.E_MEASURE;
            const labKind = kind === kinds.PROCEDURE ? kinds.LAB : kinds.E_LAB;
            const measIds = procmeas[item.id] || [];
            const { vitalMeasures, hideProcedure } = resolveProcedureMeasuresForSidebar({
              measIds,
              measures,
              procCcd: ccdId,
              procKind: kind,
              measureKind,
              lo,
              ccds,
              vitalSignCodes: vitalCcds,
              labKind,
              sideBarContent,
              flatBuckets: false,
              procedureMeta: procedures[item.id],
              cdaIndex: state.cdaIndex,
              procmeas,
              procedures,
            });
            if (hideProcedure) {
              return;
            }
            entry = {
              id: item.id,
              cdaIndex: state.cdaIndex,
              ccd: ccdId,
              desc: (ccds[ccdId] || {}).desc || String(ccdId),
              lo,
              kind,
              ...procedures[item.id],
              measures: vitalMeasures,
            };

            if (!sideBarContent[kind][entry.lo]) {
              sideBarContent[kind][entry.lo] = [];
            }
            sideBarContent[kind][entry.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEASURE || kind === kinds.MEASURE) {
        const labKind = (kind === kinds.MEASURE) ? kinds.LAB : kinds.E_LAB;
        bkad.forEach(item => {
          let ccdId = measures[item.id].ccd;
          if (ccdId) {
            const bucketKind = isVitalCcdForCda(ccdId, vitalCcds) ? kind : labKind;
            const meas = measures[item.id];
            const measLo = resolveMeasureSidebarDate(meas, item.id, {
              procmeas,
              procedures,
              measures,
              ccds,
              fallbackLo: item.lo || 'Undated',
            });
            entry = {
              dname: (ccds[ccdId] || {}).desc || String(ccdId),
              kind,
              ...meas,
              lo: measLo,
              weight: meas.weight ? meas.weight : 0,
            };
            if (!sideBarContent[bucketKind])
              sideBarContent[bucketKind] = {};
            if (!sideBarContent[bucketKind][measLo])
              sideBarContent[bucketKind][measLo] = [];
            sideBarContent[bucketKind][measLo].push(entry);
            sideBarContent[bucketKind][measLo] = _.orderBy(sideBarContent[bucketKind][measLo], 'weight', 'desc');
          }
        });

      }
      else if (kind === kinds.E_DIAGNOSIS || kind === kinds.DIAGNOSIS) {
        bkad.forEach(item => {
          let ccdId = diagnosiss[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...diagnosiss[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEDICATION || kind === kinds.MEDICATION) {
        bkad.forEach(item => {
          let ccdId = medications[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId] ? (ccds[ccdId] || {}).desc || String(ccdId) : ccdId;
            entry = {...entry, ...medications[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_VACCINE || kind === kinds.VACCINE) {
        bkad.forEach(item => {
          let ccdId = item.kind === kinds.VACCINE?vaccines[item.id].codes[0]:vaccines[item.id].ccd;
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...vaccines[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_ALLERGY || kind === kinds.ALLERGY) {
        bkad.forEach(item => {
          let ccdId = allergies[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...allergies[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }

    });

    return sideBarContent;

  },
  /**
   * returns a list of all cda ids
   */
  getCdaList (state, getters, rootState, rootGetters) {
    // pick id and recordedAt from all data
    let cdaList = [];
    if (rootGetters['login/getAllData'] &&  rootGetters['login/getAllData'].cdas) {
      rootGetters['login/getAllData'].cdas.forEach((cda,index) => {
        cdaList.push({id:cda.id, index, recordedAt: cda.recordedAt, displayName: (cda.name || cda.recordedAt)});
      });
    }
    return cdaList;
  },
  getIndexOfLastCda (state, getters, rootState) {
    const cdas = rootState.login?.phrData?.cdas;
    if (!Array.isArray(cdas) || cdas.length === 0) return -1;
    return cdas.length - 1;
  },
  getCdaById (state, getters, rootState) {

  },
  isCdaSaved (state, getters, rootState) {
    if (state.cdaIndex === INIT_NO_OP_NUM) return false;
    const cdas = rootState.login?.phrData?.cdas;
    if (!Array.isArray(cdas) || state.cdaIndex < 0 || state.cdaIndex >= cdas.length) {
      return false;
    }
    const cda = cdas[state.cdaIndex];
    return !!(cda && cda.isSaved);
  }
};

const mutations = {
  [setCda] (state, payload) {
    state.cdaId = parseInt(payload.cdaId);
    state.cdaIndex = parseInt(payload.cdaIndex);
  },
  [clearCda] (state) {
    state.cdaId = INIT_NO_OP_NUM;
    state.cdaIndex = INIT_NO_OP_NUM;
  }
};

const actions = {

  uploadCda ({commit, state, rootState}, formData) {
    return new Promise((resolve, reject) => {
      if (formData) {
        uploadFile(CDA_FILE_TYPE, formData)
            .then((response) => {
              const responseData = response.data;
              // update model
              resolve(responseData.id);
            })
            .catch((err) => {
              reject(err);
            });
      } else {
        reject('Please include a CDA file (in .xml format) you want to upload');
      }
    });
  },

  requestCdaById ({commit}, reqData) {
    const {id, index} = reqData;
    return new Promise((resolve, reject) => {
      getFileById(CDA_FILE_TYPE, id)
          .then((cdaDoc) => {
            resolve(cdaDoc);
            commit(setCda, {cdaId: id, cdaIndex: index});
          })
          .catch((err) => {
            reject(err);
          });
    });
  },

  saveCdaById ({commit}, id) {
    return new Promise((resolve, reject) => {
      requestSaveCda(id)
      .then((response)=> {
        const responseData = response.data;
        commit('login/setApplicationModel', responseData.data, {root: true});
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
    });
  },

  clearCda ({commit}) {
    return new Promise((resolve) => {
      commit(clearCda);
      resolve();
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
